import React, { useEffect, useState } from "react";
import { RJSFSchema, UiSchema } from "@rjsf/utils";
import Form from "@rjsf/bootstrap-4";
import validator from "@rjsf/validator-ajv8";
import { generateUISchema } from "./SchemaFormTemplates/generateUISchema";
import { useProfile } from "../ProfileProvider";
import "./custom-form-styles.css";
import CustomFieldTemplate from "./CustomFieldTemplate";

interface SchemaFormProps {
  schema: RJSFSchema;
  ui_Schema?: UiSchema; // Optional uiSchema prop to allow custom UI schema
  formdata?: Object;
  onChange?: Function;
  errors?: any;
  readOnly?: boolean;
}

const SchemaForm: React.FC<SchemaFormProps> = ({
  schema,
  ui_Schema = {},
  formdata,
  onChange,
  errors,
  readOnly = false,
}) => {
  const { theme } = useProfile();
  const isDarkMode = theme === "dark";
  const [formData, setFormData] = useState(formdata || {});
  const [customSchema, setCustomSchema] = useState<UiSchema>({});

  const handleChange = ({ formData }: any) => {
    if (!readOnly) {
      setFormData(formData);

      if (onChange) {
        onChange({
          target: {
            name: "schema_data",
            value: formData,
          },
        });
      }
    }
  };

  // Custom UI widgets and fields to improve styling
  const uiSchema = {
    ...ui_Schema,
    "ui:classNames": `${isDarkMode ? "dark-mode-form" : "light-mode-form"} ${
      readOnly ? "read-only-form" : ""
    }`,
    "ui:readonly": readOnly,
    "ui:disabled": readOnly,
    "ui:submitButtonOptions": {
      norender: true,
    },
  };
  if (!uiSchema["ui:order"]) {
    uiSchema["ui:order"] = ["*"];
  }

  useEffect(() => {
    const errorObj: Record<string, string> = {};
    if (Array.isArray(errors)) {
      errors.forEach((error: any) => {
        if (error.field && error.message) {
          errorObj[error.field] = error.message;
        }
      });
    }

    setCustomSchema(
      generateUISchema(schema, uiSchema, errorObj, formData, readOnly),
    );
  }, [schema, errors, JSON.stringify(formData), isDarkMode, readOnly]);

  // Prevent mouse wheel from changing number input values (AKL-500)
  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      const target = event.target as HTMLInputElement;
      if (target.type === "number" && document.activeElement === target) {
        event.preventDefault();
        target.blur();
      }
    };

    document.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      document.removeEventListener("wheel", handleWheel);
    };
  }, []);

  return (
    <div
      className={`form-preview ${isDarkMode ? "dark" : ""} ${
        readOnly ? "read-only" : ""
      }`}
    >
      {/* Display the form title separately above the form if it exists */}
      {schema.title && (
        <h2 className="text-xl mb-4 text-storm-blue dark:text-primary-off-white transition duration-300">
          {schema.title}
        </h2>
      )}

      <div className="schema-form-container">
        <Form
          schema={{
            ...schema,
            title: undefined, // Remove the title from the form itself
          }}
          uiSchema={customSchema}
          formData={formData}
          validator={validator}
          onChange={handleChange}
          templates={{ FieldTemplate: CustomFieldTemplate }}
          className={`custom-rjsf-form ${
            isDarkMode ? "dark-mode" : "light-mode"
          } ${readOnly ? "disabled-form" : ""}`}
          readonly={readOnly}
          disabled={readOnly}
        />
      </div>
    </div>
  );
};

export default SchemaForm;
